import React, { useCallback, useMemo, useState } from "react";
import { PaymentMethod } from "../../../../common/enums";
import { WorkflowMode } from "../../common/enums";
import { AchPaymentPrefInput } from "../../Components/AchPaymentPrefInput";
import { CheckPaymentPrefInput } from "../../Components/CheckPaymentPrefInput";
import { SelectPaymentMethod } from "../../Components/SelectPaymentMethod";
import { type ChildComponentProps } from "../../Components/types";
import { WirePaymentPrefInput } from "../../Components/WirePaymentPrefInput";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { type ConsolidatedPaymentPrefContextType } from "../../Context/types";
import { type BasePaymentPrefWorkflowProps } from "./types";

const getPaymentPrefForm = (paymentMethod?: PaymentMethod) => {
  switch (paymentMethod) {
    case PaymentMethod.ACH:
      return AchPaymentPrefInput;
    case PaymentMethod.Check:
      return CheckPaymentPrefInput;
    case PaymentMethod.Wire:
      return WirePaymentPrefInput;
    default:
      throw new Error("unsupported payment method");
  }
};

const getWorkflowComponents = ({
  selectedPaymentMethod,
  isExistingPaymentPref,
  workflowMode,
  prefixComponents,
  suffixComponents,
}: Pick<ConsolidatedPaymentPrefContextType, "selectedPaymentMethod" | "isExistingPaymentPref"> &
  Pick<
    BasePaymentPrefWorkflowProps,
    "prefixComponents" | "suffixComponents" | "workflowMode"
  >) => {
  const workflowComponents: React.FC<ChildComponentProps>[] = [];

  if (prefixComponents) {
    workflowComponents.push(...prefixComponents);
  }

  if (!isExistingPaymentPref) {
    if (workflowMode === WorkflowMode.Create) {
      workflowComponents.push(SelectPaymentMethod);
    }

    if (selectedPaymentMethod) {
      const paymentFormInput = getPaymentPrefForm(selectedPaymentMethod);
      workflowComponents.push(paymentFormInput);
    }
  }

  if (suffixComponents) {
    workflowComponents.push(...suffixComponents);
  }

  return workflowComponents;
};

/**
 * A base workflow component that provides the core functionality for payment preference workflows.
 * It manages step navigation, form state, and component rendering for both creation and editing
 * of payment preferences.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {() => void} props.onExit - Function called when the workflow is exited
 * @param {() => void} props.onSuccess - Function called when the workflow completes successfully
 * @param {WorkflowMode} props.workflowMode - Mode of the workflow (Create or Edit)
 * @param {React.FC<ChildComponentProps>[]} [props.prefixComponents] - Components to render before the main form
 * @param {React.FC<ChildComponentProps>[]} [props.suffixComponents] - Components to render after the main form
 *
 * @example
 * ```tsx
 * <BasePaymentPrefWorkflow
 *   onExit={() => setShowWorkflow(false)}
 *   onSuccess={handleSuccess}
 *   workflowMode={WorkflowMode.Create}
 *   prefixComponents={[SelectAvailablePaymentPrefsForm]}
 *   suffixComponents={[PaymentPrefConfirmation]}
 * />
 * ```
 *
 * @remarks
 * - Uses useConsolidatedPaymentPrefContext for state management
 * - Manages step-by-step workflow navigation
 * - Supports dynamic component injection through prefix and suffix components
 * - Handles both create and edit modes
 * - Provides consistent back/continue navigation
 * - Supports customizable back button naming
 *
 * @see {@link PaymentPrefCreationForPositionWorkflow} for creation workflow implementation
 * @see {@link PaymentPrefEditForAccountAndPositionWorkflow} for edit workflow implementation
 */
export const BasePaymentPrefWorkflow: React.FC<BasePaymentPrefWorkflowProps> = ({
  onExit,
  onSuccess,
  workflowMode,
  prefixComponents,
  suffixComponents,
}) => {
  const { selectedPaymentMethod, isExistingPaymentPref } = useConsolidatedPaymentPrefContext();
  const [step, setStep] = useState(0);

  const workflowComponents = useMemo(
    () =>
      getWorkflowComponents({
        selectedPaymentMethod,
        isExistingPaymentPref,
        workflowMode,
        prefixComponents,
        suffixComponents,
      }),
    [
      isExistingPaymentPref,
      prefixComponents,
      selectedPaymentMethod,
      suffixComponents,
      workflowMode,
    ],
  );

  const handleContinuePress = useCallback(() => {
    if (step < workflowComponents.length - 1) {
      setStep(step + 1);
    } else {
      onSuccess();
    }
  }, [onSuccess, step, workflowComponents.length]);

  const handleBackPress = useCallback(() => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      onExit();
    }
  }, [onExit, step]);

  const CurrentStep = workflowComponents[step];

  return (
    <CurrentStep
      onContinueClick={handleContinuePress}
      onBackClick={handleBackPress}
      onBackButtonName={step === 0 ? "Cancel" : undefined}
    />
  );
};
